/**
 * Frontend error handling service for API unavailability
 * Provides graceful error handling and user-friendly error messages
 */

export interface ErrorHandlingResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  unavailable?: boolean;
  suggestion?: string;
  retryAfter?: number;
}

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  backoffFactor: number;
}

export class ErrorHandlingService {
  private static instance: ErrorHandlingService;
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffFactor: 2
  };

  static getInstance(): ErrorHandlingService {
    if (!ErrorHandlingService.instance) {
      ErrorHandlingService.instance = new ErrorHandlingService();
    }
    return ErrorHandlingService.instance;
  }

  /**
   * Wraps an API call with retry logic and error handling
   */
  async withErrorHandling<T>(
    apiCall: () => Promise<T>,
    options?: {
      retryConfig?: Partial<RetryConfig>;
      errorMessage?: string;
    }
  ): Promise<ErrorHandlingResponse<T>> {
    const config = { ...this.retryConfig, ...options?.retryConfig };
    let lastError: Error | null = null;

    // Try primary API call with retries
    for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
      try {
        const result = await apiCall();
        return {
          success: true,
          data: result
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Don't retry on certain error types
        if (this.isNonRetryableError(error)) {
          break;
        }

        // Wait before retry with exponential backoff
        if (attempt < config.maxRetries) {
          const delay = Math.min(
            config.baseDelay * config.backoffFactor ** (attempt - 1),
            config.maxDelay
          );
          await this.delay(delay);
        }
      }
    }

    // Return error response
    return {
      success: false,
      error: options?.errorMessage || lastError?.message || 'Service unavailable',
      message: this.getErrorMessage(lastError),
      unavailable: true,
      suggestion: this.getErrorSuggestion(lastError)
    };
  }

  /**
   * Handles API responses that may include error indicators
   */
  handleApiResponse<T>(response: any): ErrorHandlingResponse<T> {
    return {
      success: response.success !== false,
      data: response.data,
      error: response.error,
      message: response.message,
      unavailable: response.unavailable === true,
      suggestion: response.suggestion,
      retryAfter: response.retryAfter
    };
  }

  /**
   * Creates a user-friendly error message for display
   */
  createUserMessage(response: ErrorHandlingResponse<any>): {
    type: 'success' | 'error';
    title: string;
    message: string;
    action?: string;
    retryAfter?: number;
  } {
    if (response.success) {
      return {
        type: 'success',
        title: 'Success',
        message: response.message || 'Operation completed successfully'
      };
    }

    return {
      type: 'error',
      title: response.unavailable ? 'Service Unavailable' : 'Error',
      message: response.message || response.error || 'An unexpected error occurred',
      action: response.suggestion || 'Please try again',
      retryAfter: response.retryAfter
    };
  }

  /**
   * Checks if an error should not be retried
   */
  private isNonRetryableError(error: any): boolean {
    if (!error) return false;

    const status = error.status || error.response?.status;
    const message = error.message?.toLowerCase() || '';

    // Don't retry client errors (4xx) except for specific cases
    if (status >= 400 && status < 500) {
      // Retry on 408 (timeout), 429 (rate limit), and 503 (service unavailable)
      return ![408, 429, 503].includes(status);
    }

    // Don't retry on validation errors
    if (message.includes('validation') || message.includes('invalid')) {
      return true;
    }

    // Don't retry on authentication errors
    if (message.includes('unauthorized') || message.includes('forbidden')) {
      return true;
    }

    return false;
  }

  /**
   * Gets a user-friendly error message
   */
  private getErrorMessage(error: Error | null): string {
    if (!error) return 'An unexpected error occurred';

    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return 'Network connection error - please check your internet connection';
    }

    if (message.includes('timeout')) {
      return 'Request timed out - the service may be experiencing high load';
    }

    if (message.includes('database') || message.includes('connection')) {
      return 'Database connection error - the service is temporarily unavailable';
    }

    if (message.includes('server') || message.includes('internal')) {
      return 'Server error - please try again later';
    }

    return 'Service temporarily unavailable';
  }

  /**
   * Gets a user-friendly error suggestion
   */
  private getErrorSuggestion(error: Error | null): string {
    if (!error) return 'Please try again later';

    const message = error.message.toLowerCase();

    if (message.includes('network') || message.includes('fetch')) {
      return 'Check your internet connection and try again';
    }

    if (message.includes('timeout')) {
      return 'Try again in a few moments when the service is less busy';
    }

    if (message.includes('database') || message.includes('connection')) {
      return 'The service is being restored - please try again in a few minutes';
    }

    return 'Please try again later or contact support if the issue persists';
  }

  /**
   * Delays execution for the specified number of milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Checks if the API is available
   */
  async checkApiHealth(): Promise<{
    available: boolean;
    recentErrors: number;
    message: string;
  }> {
    try {
      const response = await fetch('/api/error-handling/status');
      const data = await response.json();

      if (response.ok && data.success) {
        return {
          available: true,
          recentErrors: data.data.stats.recentErrors || 0,
          message: data.data.status.healthy ? 'All systems operational' : 'Some services experiencing issues'
        };
      }

      return {
        available: false,
        recentErrors: 0,
        message: 'API health check failed'
      };
    } catch (error) {
      return {
        available: false,
        recentErrors: 0,
        message: 'Cannot reach API'
      };
    }
  }

  /**
   * Gets error handling statistics
   */
  async getErrorStats(): Promise<{
    totalErrors: number;
    recentErrors: number;
    errorsByEndpoint: Record<string, number>;
  } | null> {
    try {
      const response = await fetch('/api/error-handling/status');
      const data = await response.json();

      if (response.ok && data.success) {
        return {
          totalErrors: data.data.stats.totalErrors,
          recentErrors: data.data.stats.recentErrors,
          errorsByEndpoint: data.data.stats.errorsByEndpoint
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
export const errorHandlingService = ErrorHandlingService.getInstance();